import { Link } from "react-router-dom";
import { Users, Building2, Search, Handshake, Shield, Clock, CheckCircle2, ArrowRight, Briefcase, UserCheck, HeadphonesIcon, Award, ChevronRight, Phone, Mail, MapPin } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/de753d50-e73a-4c45-bc5e-daf327ccb96f/images/56ede6aefdefd30c15a9a71f56bf50fad3741b650090c4a1901f31f301b57cb7.png";

const stats = [
  { number: "500+", label: "Placements" },
  { number: "120+", label: "Client Companies" },
  { number: "15+", label: "Industries Served" },
];

const services = [
  { icon: Users, title: "Permanent Staffing", desc: "Find the right talent for long-term roles across all levels and functions." },
  { icon: Briefcase, title: "Contract Staffing", desc: "Flexible workforce solutions for project-based and seasonal needs." },
  { icon: Search, title: "Executive Search", desc: "Identify and recruit C-suite and senior leadership talent." },
  { icon: Building2, title: "Bulk Recruitment", desc: "Large-scale hiring drives for manufacturing, retail, and BPO sectors." },
  { icon: Handshake, title: "HR Consulting", desc: "Strategic HR advisory on policy, compliance, and organizational design." },
  { icon: Shield, title: "Payroll Management", desc: "End-to-end payroll processing, compliance, and statutory management." },
];

const processSteps = [
  { num: "01", title: "Understand", desc: "We learn your requirements, culture, and hiring goals." },
  { num: "02", title: "Search & Screen", desc: "Our team sources and screens candidates from our vast network." },
  { num: "03", title: "Match & Present", desc: "We present the best-fit candidates with detailed profiles." },
  { num: "04", title: "Place & Support", desc: "Seamless onboarding with ongoing support for both parties." },
];

const industries = [
  "IT", "Banking & Finance", "Manufacturing", "Healthcare", "Retail & FMCG",
  "Construction", "Logistics", "Education", "Hospitality", "Oil & Gas",
  "Telecom", "Automobile", "Pharma", "Government", "NGO",
];

const whyChooseUs = [
  { icon: UserCheck, title: "Precision Matching", desc: "AI-enhanced screening meets human insight for perfect culture and skill fit." },
  { icon: Clock, title: "Fast Turnaround", desc: "Average time-to-fill of 15 days — keeping your business moving." },
  { icon: HeadphonesIcon, title: "Dedicated Support", desc: "A single point of contact throughout the hiring journey." },
  { icon: Award, title: "Compliance Assured", desc: "Full statutory compliance across all staffing engagements." },
];

export default function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative bg-white overflow-hidden">
        <div className="absolute inset-0 hero-pattern" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-4 block">India's Trusted HR Partner</span>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 uppercase mb-6">
                Bridging Talent<br />
                <span className="text-[var(--sb-orange)]">with Opportunity</span>
              </h1>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                Comprehensive manpower solutions across India — connecting businesses with exceptional talent and helping professionals find their ideal career path.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/employers"
                  data-testid="hero-hire-talent-btn"
                  className="bg-[var(--sb-blue)] text-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[var(--sb-blue-hover)] transition-all hover:-translate-y-1 inline-flex items-center gap-2"
                >
                  Hire Talent <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/careers"
                  data-testid="hero-find-job-btn"
                  className="bg-[var(--sb-green)] text-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[#2E7E2E] transition-all hover:-translate-y-1 inline-flex items-center gap-2"
                >
                  Find a Job <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={2} className="hidden lg:block">
              <img src="/hero-handshake.jpg" alt="HR Solutions" className="shadow-2xl rounded-3xl w-full max-w-lg mx-auto transform hover:scale-105 transition-duration-300" />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section data-testid="stats-section" className="bg-[var(--sb-blue)] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 text-center">
            {stats.map((stat) => (
              <ScrollReveal key={stat.label}>
                <div className="text-5xl md:text-6xl font-bold font-heading mb-2 count-animate">{stat.number}</div>
                <div className="text-sm font-bold uppercase tracking-[0.15em] text-blue-200">{stat.label}</div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">What We Do</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Our Services</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((svc, i) => (
              <ScrollReveal key={svc.title} delay={i}>
                <div data-testid={`service-card-${i}`} className="bg-white border border-slate-200 p-8 transition-all duration-300 hover:shadow-[0_10px_40px_-15px_rgba(26,95,168,0.15)] hover:-translate-y-1 hover:border-[var(--sb-blue)]/30 h-full">
                  <div className="w-14 h-14 bg-slate-50 flex items-center justify-center mb-6 text-[var(--sb-blue)]">
                    <svc.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">{svc.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services-btn" className="bg-transparent text-[var(--sb-blue)] border-2 border-[var(--sb-blue)] px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[var(--sb-blue)] hover:text-white transition-all hover:-translate-y-1 inline-flex items-center gap-2">
              View All Services <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Two-Audience Section */}
      <section data-testid="audience-section" className="py-20 md:py-32 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Who We Serve</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Two Paths, One Bridge</h2>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Employers */}
            <ScrollReveal>
              <div className="bg-white border border-slate-200 p-8 md:p-10 h-full">
                <div className="w-14 h-14 bg-[var(--sb-blue)] flex items-center justify-center mb-6 text-white">
                  <Building2 className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 uppercase mb-4">For Employers</h3>
                <ul className="space-y-3 mb-8">
                  {["Access pre-screened, interview-ready candidates", "Reduce time-to-hire by up to 60%", "Flexible staffing models — permanent, contract, or bulk", "Industry-specific talent pools", "Full compliance and payroll support"].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-600 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-[var(--sb-green)] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/employers" data-testid="employer-cta-btn" className="bg-[var(--sb-blue)] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[var(--sb-blue-hover)] transition-all hover:-translate-y-1 inline-flex items-center gap-2">
                  Hire Talent <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </ScrollReveal>

            {/* Job Seekers */}
            <ScrollReveal delay={1}>
              <div className="bg-white border border-slate-200 p-8 md:p-10 h-full">
                <div className="w-14 h-14 bg-[var(--sb-green)] flex items-center justify-center mb-6 text-white">
                  <Users className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 uppercase mb-4">For Job Seekers</h3>
                <ul className="space-y-3 mb-8">
                  {["Access exclusive opportunities across 15+ industries", "Free career consultation and resume review", "Dedicated placement coordinator", "Confidential job search support", "Post-placement support and guidance"].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-600 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-[var(--sb-green)] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/careers" data-testid="jobseeker-cta-btn" className="bg-[var(--sb-green)] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#2E7E2E] transition-all hover:-translate-y-1 inline-flex items-center gap-2">
                  Find a Job <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">How It Works</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Our 4-Step Process</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, i) => (
              <ScrollReveal key={step.num} delay={i}>
                <div className="text-center relative">
                  <div className="w-14 h-14 bg-[var(--sb-orange)] text-white flex items-center justify-center mx-auto mb-6 font-heading font-bold text-xl">
                    {step.num}
                  </div>
                  <h4 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">{step.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section data-testid="industries-section" className="py-20 md:py-32 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-12">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Sectors We Cover</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Industries We Serve</h2>
          </ScrollReveal>
          <ScrollReveal>
            <div className="flex flex-wrap gap-3 justify-center">
              {industries.map((ind) => (
                <span key={ind} data-testid={`industry-tag-${ind.toLowerCase().replace(/[&\s]+/g, "-")}`} className="inline-block px-5 py-2.5 bg-white text-slate-700 text-sm font-medium hover:bg-[var(--sb-blue)] hover:text-white transition-colors cursor-pointer border border-slate-200">
                  {ind}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Our Advantage</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Why Choose Us</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, i) => (
              <ScrollReveal key={item.title} delay={i}>
                <div data-testid={`why-card-${i}`} className="bg-white border border-slate-200 p-8 transition-all duration-300 hover:shadow-[0_10px_40px_-15px_rgba(26,95,168,0.15)] hover:-translate-y-1 hover:border-[var(--sb-blue)]/30 text-center h-full">
                  <div className="w-14 h-14 bg-slate-50 flex items-center justify-center mx-auto mb-6 text-[var(--sb-blue)]">
                    <item.icon className="w-7 h-7" />
                  </div>
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-3">{item.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Preview */}
      <section data-testid="contact-preview-section" className="py-20 md:py-32 bg-[#0D2E54] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-12">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-orange)] mb-3 block">Get in Touch</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white uppercase mb-4">Ready to Get Started?</h2>
            <p className="text-blue-200 text-base md:text-lg max-w-2xl mx-auto">Whether you're hiring or job-seeking, we're here to help you bridge the gap.</p>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <ScrollReveal>
              <div className="bg-white/5 border border-white/10 p-8 text-center">
                <Phone className="w-8 h-8 text-[var(--sb-orange)] mx-auto mb-4" />
                <h4 className="font-heading font-bold text-lg uppercase mb-2">Call Us</h4>
                <a href="tel:+919214463185" className="text-blue-200 hover:text-white transition-colors">+91 92144 63185</a>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={1}>
              <div className="bg-white/5 border border-white/10 p-8 text-center">
                <Mail className="w-8 h-8 text-[var(--sb-orange)] mx-auto mb-4" />
                <h4 className="font-heading font-bold text-lg uppercase mb-2">Email Us</h4>
                <a href="mailto:info@skillsbridge.co.in" className="text-blue-200 hover:text-white transition-colors">info@skillsbridge.co.in</a>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={2}>
              <div className="bg-white/5 border border-white/10 p-8 text-center">
                <MapPin className="w-8 h-8 text-[var(--sb-orange)] mx-auto mb-4" />
                <h4 className="font-heading font-bold text-lg uppercase mb-2">Visit Us</h4>
                <span className="text-blue-200">India</span>
              </div>
            </ScrollReveal>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="mailto:info@skillsbridge.co.in" data-testid="contact-send-email-btn" className="bg-[var(--sb-orange)] text-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[#C9661A] transition-all hover:-translate-y-1 inline-flex items-center gap-2">
              <Mail className="w-5 h-5" /> Send Email
            </a>
            <a href="tel:+919214463185" data-testid="contact-call-now-btn" className="bg-transparent text-white border-2 border-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-white hover:text-[#0D2E54] transition-all hover:-translate-y-1 inline-flex items-center gap-2">
              <Phone className="w-5 h-5" /> Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
